import React, { useEffect, useState } from 'react'
import { JitsiMeeting } from '@jitsi/react-sdk';
import TopBar from '../../components/TopBar';
import { useLocation, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import axiosInstance from '../../axiosInstance';
import { FaVideo, FaMicrophone, FaSpinner, FaExclamationTriangle, FaCheckCircle, FaClock } from 'react-icons/fa';
import { validateRoomName } from '../../utils';

// Browser compatibility check
const checkBrowserCompatibility = () => {
    const userAgent = navigator.userAgent;
    const isEdge = userAgent.includes('Edge') || userAgent.includes('Edg/');
    const isChrome = userAgent.includes('Chrome') && !isEdge;
    const isFirefox = userAgent.includes('Firefox');
    const isSafari = userAgent.includes('Safari') && !isChrome && !isEdge;
    
    // Check for WebRTC support
    const hasWebRTC = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasRTCPeerConnection = !!(window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection);
    
    return {
        isSupported: hasWebRTC && hasRTCPeerConnection,
        browserName: isEdge ? 'Edge' : isChrome ? 'Chrome' : isFirefox ? 'Firefox' : isSafari ? 'Safari' : 'Unknown',
        isEdge,
        isChrome,
        isFirefox,
        isSafari,
        hasWebRTC,
        hasRTCPeerConnection
    };
};


export default function VideoAppointment() {
    const [meetingUrl, setMeetingUrl] = useState('');
    const [roomName, setRoomName] = useState('');
    const [jwtToken, setJwtToken] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [mode, setMode] = useState('');
    const [appointmentData, setAppointmentData] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [browserCompatibility, setBrowserCompatibility] = useState(null);
    const location = useLocation().state;
    const navigate = useNavigate();

    // Check browser compatibility on component mount
    useEffect(() => {
        const compatibility = checkBrowserCompatibility();
        setBrowserCompatibility(compatibility);
        
        if (!compatibility.isSupported) {
            console.warn('Browser may not fully support WebRTC features:', compatibility);
        }
    }, []);

    const check = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
            const userId = encodeURIComponent(userInfo.id);
            const id = encodeURIComponent(location);
            if (!userId || !id) throw new Error("Missing userId or location");
            const { data } = await axiosInstance.get(`/visits/appointments/check?patient=${userId}&id=${id}`);
            console.log('data data', data);
            console.log('data location', location);
            if (data?.appointment?.length <= 0) {
                swal({
                    text: "You do not have any appointment today",
                    title: "No Appointment",
                    icon: "warning",
                    buttons: ["Cancel", "OK"],
                }).then(async (confirm) => {
                    return navigate('/homedashboard');
                });
            } 
            
            const appointmentUrl = data.data?.appointment?.appt_url;
            if (!appointmentUrl) {
                throw new Error("No appointment URL found");
            }
            
            console.log('Full appointment URL:', appointmentUrl);
            
            try {
                const urlObj = new URL(appointmentUrl);
                const roomNameFromUrl = urlObj.pathname.replace(/^\/+/, '');
                const jwtFromUrl = urlObj.searchParams.get('jwt');
                
                if (!jwtFromUrl) {
                    throw new Error("No JWT token found in appointment URL");
                }
                
                // Validate room name format and security
                if (!validateRoomName(roomNameFromUrl)) {
                    console.warn('Invalid or insecure room name detected:', roomNameFromUrl);
                    swal({
                        text: "Invalid meeting room configuration. Please contact support.",
                        title: "Security Error",
                        icon: "error",
                        buttons: ["OK"],
                    }).then(() => {
                        return navigate('/homedashboard');
                    });
                    return;
                }
                
                setMeetingUrl(appointmentUrl);
                setRoomName(roomNameFromUrl);
                setJwtToken(jwtFromUrl);
                setMode(data?.data?.appointment?.appt_mode);
                setAppointmentData(data?.data?.appointment);
                setConnectionStatus('ready');
                
            } catch (urlError) {
                console.error('Error parsing appointment URL:', urlError);
                swal({
                    text: "Invalid appointment URL format. Please contact support.",
                    title: "URL Error",
                    icon: "error",
                    buttons: ["OK"],
                }).then(() => {
                    return navigate('/homedashboard');
                });
                return;
            }

        } catch (error) {
            console.error('Error checking appointment:', error);
            setConnectionStatus('error');
        }
    } 
    console.log('Meeting URL:', meetingUrl);
    console.log('Room Name:', roomName);
    console.log('JWT Token:', jwtToken ? 'Present' : 'Missing');
    
    useEffect(() => {
        check();
    }, [location]);
    

    if (!meetingUrl || !jwtToken || connectionStatus === 'connecting') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
                <TopBar title={"Live Consultation"} url={'/homedashboard'} />
                
                <div className="flex flex-col items-center justify-center px-4 py-16">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
                        {/* Browser Compatibility Warning for Edge */}
                        {browserCompatibility?.isEdge && (
                            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <div className="flex items-center justify-center space-x-2 mb-2">
                                    <FaExclamationTriangle className="text-amber-600" />
                                    <span className="text-sm font-semibold text-amber-800">Edge Browser Detected</span>
                                </div>
                                <p className="text-xs text-amber-700">
                                    You're using Microsoft Edge. The video consultation will work, but for the best experience, we recommend using Chrome or Firefox.
                                </p>
                            </div>
                        )}
                        
                        {/* Loading Animation */}
                        <div className="relative mb-8">
                            <div className="w-24 h-24 mx-auto bg-gradient-to-r from-primary100 to-blue-600 rounded-full flex items-center justify-center">
                                <FaVideo className="text-white text-3xl" />
                            </div>
                            <div className="absolute inset-0 w-24 h-24 mx-auto border-4 border-primary100 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        
                        {/* Status Messages */}
                        <div className="space-y-4">
                            <h2 className="text-2xl font-bold text-gray-800">
                                Preparing Your Consultation
                            </h2>
                            
                            <div className="space-y-3">
                                <div className="flex items-center justify-center space-x-3">
                                    <FaCheckCircle className="text-green-500" />
                                    <span className="text-gray-600">Verifying appointment</span>
                                </div>
                                <div className="flex items-center justify-center space-x-3">
                                    <FaSpinner className="text-primary100 animate-spin" />
                                    <span className="text-gray-600">Connecting to meeting room</span>
                                </div>
                                <div className="flex items-center justify-center space-x-3">
                                    <FaClock className="text-gray-400" />
                                    <span className="text-gray-600">Setting up audio/video</span>
                                </div>
                            </div>
                            
                            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                                <p className="text-sm text-blue-700">
                                    Please ensure your camera and microphone permissions are enabled
                                </p>
                                {browserCompatibility?.isEdge && (
                                    <p className="text-xs text-blue-600 mt-2">
                                        Edge users: Click "Allow" when prompted for camera/microphone access
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State
    if (connectionStatus === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50">
                <TopBar title={"Connection Error"} url={'/homedashboard'} />
                
                <div className="flex flex-col items-center justify-center px-4 py-16">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
                        <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                            <FaExclamationTriangle className="text-red-500 text-3xl" />
                        </div>
                        
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">
                            Connection Failed
                        </h2>
                        
                        <p className="text-gray-600 mb-6">
                            We're having trouble connecting to your appointment. Please check your internet connection and try again.
                        </p>
                        
                        <button 
                            onClick={() => window.location.reload()}
                            className="w-full bg-primary100 text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary100 hover:bg-opacity-90 transition-all duration-200"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-gray-900">
            <TopBar title={"Live Consultation"} url={'/homedashboard'} />
            
            {/* Connection Status Overlay */}
            {isLoading && (
                <div className="absolute inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center">
                        <div className="w-16 h-16 mx-auto bg-gradient-to-r from-primary100 to-blue-600 rounded-full flex items-center justify-center mb-4">
                            <FaVideo className="text-white text-2xl" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            Joining Meeting
                        </h3>
                        <p className="text-gray-600 text-sm mb-4">
                            Please wait while we connect you to your consultation
                        </p>
                        <div className="flex items-center justify-center space-x-2">
                            <div className="w-2 h-2 bg-primary100 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-primary100 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-primary100 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Meeting Interface */}
            <div className="relative h-full">
                {/* Meeting Info Bar */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary100 to-blue-600 text-white p-4 z-40">
                    <div className="flex items-center justify-between max-w-4xl mx-auto">
                        <div className="flex items-center space-x-3">
                            {mode === 'audio' ? (
                                <FaMicrophone className="text-xl" />
                            ) : (
                                <FaVideo className="text-xl" />
                            )}
                            <div>
                                <h3 className="font-semibold">
                                    {mode === 'audio' ? 'Audio Consultation' : 'Video Consultation'}
                                </h3>
                                <p className="text-sm opacity-90">
                                    {appointmentData?.clinic_name || 'Medical Consultation'}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-sm">Live</span>
                        </div>
                    </div>
                </div>

                {/* Jitsi Meeting Component */}
                <div className="pt-20 h-full">
                    <JitsiMeeting
                        domain={new URL(meetingUrl).hostname}
                        roomName={roomName}
                        jwt={jwtToken}
                        configOverwrite={{
                            startWithAudioMuted: false,
                            disableModeratorIndicator: false,
                            startScreenSharing: true,
                            enableEmailInStats: false,
                            defaultLogoUrl: 'https://misas.com.ng/assets/images/logos/misaslogo.png',
                            disableDeepLinking: true,
                            startAudioOnly: mode === 'audio' ? true : false,
                            prejoinPageEnabled: false,
                            requireDisplayName: true,
                            enableWelcomePage: false,
                            enableClosePage: true,
                            disableInviteFunctions: true,
                            enableUserRolesBasedOnToken: false,
                            enableLobbyChat: false,
                            disablePolls: true,
                            enableInsecureRoomNameWarning: false,
                            enableNoisyMicDetection: true,
                            enableAutomaticUrlCopy: false,
                            readOnlyName: true,
                            resolution: browserCompatibility?.isEdge ? 480 : 720,
                            constraints: {
                                video: {
                                    height: {
                                        ideal: browserCompatibility?.isEdge ? 480 : 720,
                                        max: browserCompatibility?.isEdge ? 480 : 720,
                                        min: 180
                                    },
                                    width: {
                                        ideal: browserCompatibility?.isEdge ? 640 : 1280,
                                        max: browserCompatibility?.isEdge ? 640 : 1280,
                                        min: 320
                                    }
                                }
                            },
                            p2p: {
                                enabled: !browserCompatibility?.isEdge // Disable P2P for Edge for better stability
                            },
                            testing: {
                                enableFirefoxSimulcast: false
                            },
                            // Edge-specific optimizations
                            ...(browserCompatibility?.isEdge && {
                                disableAudioLevels: true,
                                enableTalkWhileMuted: false,
                                disableSimulcast: true,
                                enableLayerSuspension: false
                            })
                        }}
                        interfaceConfigOverwrite={{
                            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
                            BRAND_WATERMARK_LINK: 'https://misas.com.ng/assets/images/logos/misaslogo.png',
                            DEFAULT_WELCOME_PAGE_LOGO_URL: 'https://misas.com.ng/assets/images/logos/misaslogo.png',
                            DEFAULT_BACKGROUND: '#087F4C',
                            SHOW_BRAND_WATERMARK: false,
                            SHOW_JITSI_WATERMARK: false,
                            POLICY_LOGO: 'https://misas.com.ng/assets/images/logos/misaslogo.png',
                            PROVIDER_NAME: 'Misas',
                            HIDE_DEEP_LINKING_LOGO: true,
                            MOBILE_APP_PROMO: false,
                            DISABLE_FOCUS_INDICATOR: true,
                            DISABLE_VIDEO_BACKGROUND: false,
                            TOOLBAR_BUTTONS: [
                                'camera',
                                'chat',
                                'closedcaptions',
                                'desktop',
                                'fullscreen',
                                'hangup',
                                'microphone',
                                'raisehand',
                                'settings',
                                'toggle-camera',
                            ],
                        }}
                        userInfo={{
                            displayName: `${JSON.parse(localStorage.getItem("userInfo"))?.firstname} ${JSON.parse(localStorage.getItem("userInfo"))?.lastname}`,
                            email: JSON.parse(localStorage.getItem("userInfo"))?.email,
                            role: 'moderator'
                        }}
                        onApiReady={(externalApi) => {
                            try {
                                console.log('Jitsi API ready with JWT authentication');
                                console.log('Browser compatibility:', browserCompatibility);
                                
                                externalApi.executeCommand('subject', 'Promise Medical Consultation');
                                externalApi.executeCommand('toggleLobby', false);
                                
                                // Edge-specific configurations
                                if (browserCompatibility?.isEdge) {
                                    console.log('Applying Edge-specific optimizations');
                                    // Disable some features that may cause issues in Edge
                                    externalApi.executeCommand('toggleCamera');
                                    setTimeout(() => {
                                        externalApi.executeCommand('toggleCamera');
                                    }, 1000);
                                }
                                
                                if (jwtToken) {
                                    try {
                                        externalApi.executeCommand('startRecording', {
                                            mode: 'file'
                                        });
                                    } catch (recordingError) {
                                        console.warn('Recording not available or not permitted:', recordingError);
                                    }
                                }
                                
                                setConnectionStatus('connected');
                                
                                // Longer timeout for Edge browser
                                const loadingTimeout = browserCompatibility?.isEdge ? 5000 : 3000;
                                setTimeout(() => {
                                    setIsLoading(false);
                                }, loadingTimeout); 
                                
                            } catch (error) {
                                console.error('Error in onApiReady:', error);
                                setIsLoading(false);
                            }
                        }}
                        getIFrameRef={(iframeRef) => { 
                            iframeRef.style.height = '100%';
                            iframeRef.style.border = 'none';
                            iframeRef.allow = "camera;microphone;display-capture;clipboard-write;fullscreen";
                        }}
                    />
                </div>
            </div>
        </div>
    )
}
